import { BET, BETS, type BetDef, type BetId } from '@somnhold/content';
import { barrelsWon, betOpen, betsOf, groupLevel, type CampaignState } from '@somnhold/sim';
import { t } from '../i18n.ts';
import { h } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';
import { portrait } from './saga-panel.ts';
import type { CampaignService } from './service.ts';

/**
 * Brann and Hilde's bets (docs/SAGAS.md): a section of the chronicle with one card per bet, the two of
 * them arguing over it, and the way to take it on. A won bet stays marked (its barrel is in the bastion). The open gate
 * (bet 9) is the one both bet on: no barrel changes hands, Hilde forges a plaque with the names instead.
 */

const other = (who: BetDef['favor']): 'brann' | 'hilde' => (who === 'brann' ? 'hilde' : 'brann');

/** Who bets the group can do it, and who that it can't (both for the open gate). */
const favorLine = (b: BetDef): string => (b.favor === 'both' ? t('bet.favorBoth') : t('bet.favor', { yes: t(`npc.${b.favor}`), no: t(`npc.${other(b.favor)}`) }));

const card = (s: CampaignState, b: BetDef, play: (id: BetId) => void): HTMLElement => {
  const won = betsOf(s).won.includes(b.id);
  const open = betOpen(s, b.id);
  return h(
    `div.bet-card${won ? '.won' : ''}${open ? '' : '.locked'}`,
    { 'data-test': `bet-${b.id}` },
    h('div.bet-head', {}, h('b', {}, t(`bet.${b.id}.title`)), won ? h('span.bet-won', {}, h('span', { html: ICONS.check }), t('bet.won')) : ''),
    h('p.bet-rule', {}, t(`bet.${b.id}.rule`, { n: BET.alone.max })),
    h(
      'div.bet-talk',
      {},
      ...b.lines.map(([who, key]) => h(`p.bet-line.${who}`, {}, portrait(who), h('span', {}, h('b', {}, t(`npc.${who}`)), ' ', t(`bet.${b.id}.${key}`)))),
    ),
    h(
      'div.bet-foot',
      {},
      h('span.small', {}, favorLine(b)),
      open
        ? h('button.btn', { 'data-test': `bet-play-${b.id}`, onclick: () => play(b.id) }, won ? t('bet.again') : t('bet.take'))
        : h('span.bet-lock', {}, h('span', { html: ICONS.lock }), t('bet.locked', { n: b.minGroup, g: groupLevel(s, s.clock) })),
    ),
  );
};

/** The bets section: shown once the group reaches the first bet's level; the next locked one is shown as a tease. */
export const betsSection = (svc: CampaignService, play: (id: BetId) => void): HTMLElement | null => {
  const s = svc.state;
  const open = BETS.filter((b) => betOpen(s, b.id));
  if (!open.length) return null;
  const next = BETS.find((b) => !betOpen(s, b.id));
  const won = barrelsWon(s);
  return h(
    'div.bets',
    { 'data-test': 'bets' },
    h('span.label', {}, t('bet.title')),
    h('p.small', {}, won ? t('bet.barrels', { n: won }) : t('bet.intro')),
    ...open.map((b) => card(s, b, play)),
    next ? card(s, next, play) : '',
  );
};

/** How a bet ended: who pays whom, or who laughs. */
export const betOutcome = (id: BetId, won: boolean, first: boolean): string => {
  const b = BETS.find((x) => x.id === id) as BetDef;
  // Both bet on the group: nobody pays, nobody laughs.
  if (b.favor === 'both') return t(!won ? 'bet.result.bothLost' : first ? 'bet.result.both' : 'bet.result.bothAgain');
  const yes = t(`npc.${b.favor}`);
  const no = t(`npc.${other(b.favor)}`);
  if (!won) return t('bet.result.lost', { no });
  return first ? t('bet.result.won', { yes, no }) : t('bet.result.again', { yes, no });
};

/** Ticks as m:ss. */
const mmss = (ticks: number): string => {
  const sec = Math.ceil(Math.max(0, ticks) / 12);
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
};

/** How the race went: the time it took (against Brann's boast), or the clock run out. */
export type RaceOutcome = { ticks: number; brann: number; out: boolean };

/** The card after a bet: who pays whom (or who laughs), how close a hundred at a blow came, how the race went. */
export const betResult = (id: BetId, won: boolean, first: boolean, title: string, onBack: (el: HTMLElement) => void, best?: number, race?: RaceOutcome): HTMLElement => {
  const b = BETS.find((x) => x.id === id) as BetDef;
  // The one who doubted speaks: paying up, or laughing (Brann, for the open gate: it was his doubt to prove wrong).
  const speaker = b.favor === 'both' ? 'brann' : other(b.favor);
  const plaque = b.favor === 'both';
  const el: HTMLElement = h(
    'div.modal-back.saga-end',
    { 'data-test': 'bet-result' },
    h(
      'div.modal',
      { role: 'dialog', 'aria-modal': 'true' },
      h('header', {}, h('h2', {}, won ? t('bet.result.wonTitle') : t('bet.result.lostTitle')), h('p', {}, title)),
      h(
        'div.body',
        {},
        h('p.bet-line', {}, portrait(speaker), h('span', {}, betOutcome(id, won, first))),
        !won && id === 'unscathed' ? h('p.small', {}, t('bet.unscathed.lost')) : '',
        !won && id === 'alone' ? h('p.small', {}, t('bet.alone.lost')) : '',
        best !== undefined ? h('p.small', {}, t('bet.result.best', { n: Math.min(best, BET.burst.count), of: BET.burst.count })) : '',
        race && !won && race.out ? h('p.small', { 'data-test': 'race-out' }, t('bet.race.out')) : '',
        race && won ? h('p.small', { 'data-test': 'race-time' }, t(race.ticks <= race.brann ? 'bet.race.beat' : 'bet.race.slower', { t: mmss(race.ticks), b: mmss(race.brann) })) : '',
        // The prize, in sight: the barrel for the pile (the first time) and the experience.
        won
          ? h(
              'div.bet-prize',
              { 'data-test': 'bet-prize' },
              first ? h('span.cask', { html: plaque ? ICONS.shield : ICONS.cask }) : '',
              h(
                'div',
                {},
                first ? h('b', {}, t(plaque ? 'bet.result.plaqueTitle' : 'bet.result.barrelTitle')) : '',
                h('span.small', {}, `${first ? `${t(plaque ? 'bet.result.plaque' : 'bet.result.barrel')} ` : ''}${t('bet.result.xp', { n: first ? BET.xp : Math.floor(BET.xp / 2) })}`),
              ),
            )
          : '',
      ),
      h('footer', {}, h('button.btn', { 'data-test': 'bet-back', onclick: () => onBack(el) }, t('saga.result.back'))),
    ),
  );
  return el;
};
